import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import logo from '../assets/logo.svg';
import bgImage from '../assets/bg.webp';

/**
 * ForgotPassword page for requesting a password reset.
 * Sends reset link to user's email via /api/auth/forgot-password.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();

  // Preserve the continue URL for after password reset
  const continueUrl = searchParams.get('continue') || '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Client-side validation
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          return_to: continueUrl || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.error || 'Failed to send reset email';
        setError(errorMsg);
        toast.error(errorMsg);
        return;
      }

      toast.success('Reset link sent! Check your inbox.');
      setSubmitted(true);
    } catch (err) {
      const errorMsg = 'Network error. Please try again.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Build login URL preserving continue param
  const loginUrl = continueUrl
    ? `/auth/login?continue=${encodeURIComponent(continueUrl)}`
    : '/auth/login';

  if (submitted) {
    return (
      <div
        className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
        style={{ backgroundImage: `url(${bgImage})` }}
      >
        <img src={logo} alt="LevelUp" className="h-10 mt-8" />
        <div className="flex-1 flex items-center justify-center w-full">
          <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md text-center">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-6 h-6 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Check your email
          </h1>
          <p className="text-sm text-gray-500 mb-6">
            If an account exists for <strong>{email}</strong>, we've sent a
            password reset link. Please check your inbox and spam folder.
          </p>
          <Link
            to={loginUrl}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Back to sign in
          </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[rgb(24,24,24)] bg-cover bg-center flex flex-col items-center p-4"
      style={{ backgroundImage: `url(${bgImage})` }}
    >
      <img src={logo} alt="LevelUp" className="h-10 mt-8" />
      <div className="flex-1 flex items-center justify-center w-full">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">
            Reset your password
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="you@example.com"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-green-700 text-white py-2 px-4 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Sending...' : 'Send reset link'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to={loginUrl}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Back to sign in
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}
